import { useState } from "react"
import { useProjectByTenantQuery } from "@/v2-backoffice-client/project/byTenant/project.byTenant.query"
import { useUserTenantQuery } from "@/v2-backoffice-client/user/tenant/user.tenant.query"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

/** The wire shape of one active-context write (UserActiveProjectInput). */
export type UserActiveContextValues = {
  TenantId: number
  // BigInt project id as a string (codegen shape); null clears the project.
  ProjectId: string | null
  // Only read by the server when ProjectId is null — a project carries its
  // own country. Null clears a standalone country.
  CountryCode: number | null
}

export type UserActiveContext = ReturnType<typeof useUserActiveContext>

/**
 * The user's active team, project and country as one state machine: the team
 * scopes the project list, the project settles the country. Owns the two
 * queries the pickers need and the resolution rules the server applies, so
 * the fields render and the submit sends the same answer.
 *
 * State initializes from the user's stored values in plain `useState`
 * initializers — the caller mounts inside `Dialog.Content`, which remounts
 * per opening, so every opening reads fresh and a background cache write
 * mid-edit never clobbers what the operator is doing.
 */
export function useUserActiveContext(user: UserFragment) {
  const [tenantId, setTenantId] = useState<number | null>(
    user.ActiveTenantId ?? null,
  )
  const [projectId, setProjectId] = useState<string | null>(
    user.ActiveProjectId ?? null,
  )
  const [countryCode, setCountryCode] = useState<number | null>(
    user.ActiveCountryCode ?? null,
  )

  const { data: teamData } = useUserTenantQuery({
    variables: { userId: user.Id, onlyDeleted: false },
  })
  const { data: projectData, loading: projectsLoading } =
    useProjectByTenantQuery({
      variables: { tenantId: tenantId ?? 0 },
      skip: !tenantId,
    })

  const userTenants = [...(teamData?.getUserTenantsByUserId ?? [])].sort(
    (a, b) => (a.Tenant?.Name ?? "").localeCompare(b.Tenant?.Name ?? ""),
  )
  const projects = projectData?.getProjectsByTenantId ?? []

  // The user's stored project can have been deleted or moved off the team
  // since. Reading it as no project keeps the fields honest and lets an
  // unrelated name edit through — sending the dangling id would only earn a
  // rejection from the server, which validates the same thing.
  const projectsResolved =
    tenantId != null && !projectsLoading && projectData !== undefined
  const selectedProject = projects.find((p) => p.Id === projectId)
  const staleProject =
    projectId !== null && projectsResolved && !selectedProject
  const effectiveProjectId = staleProject ? null : projectId

  // A project carries its own country and the server copies it over, so the
  // country is the operator's to choose only for a user left without one.
  const countryFollowsProject = effectiveProjectId !== null
  const effectiveCountryCode = countryFollowsProject
    ? (selectedProject?.CountryCode ?? null)
    : countryCode

  function selectTenant(next: string) {
    const nextTenantId = Number(next)
    setTenantId(nextTenantId)
    // Switching back to the user's current team restores their project; any
    // other team starts with no project selected (the lists are disjoint).
    setProjectId(
      nextTenantId === user.ActiveTenantId
        ? (user.ActiveProjectId ?? null)
        : null,
    )
  }

  // What the fields display is what a save writes — the stale project reads
  // as cleared, the country as the project's — so an unchanged dialog
  // compares equal to the stored values and skips the write entirely.
  const changed =
    tenantId !== (user.ActiveTenantId ?? null) ||
    effectiveProjectId !== (user.ActiveProjectId ?? null) ||
    effectiveCountryCode !== (user.ActiveCountryCode ?? null)

  // The write, or null when there is nothing to write: the mutation requires
  // a team, so a user with no active team who still hasn't been given one
  // here is simply left untouched — as is an untouched dialog, so an
  // ordinary name edit doesn't churn the session cache.
  const values: UserActiveContextValues | null =
    tenantId !== null && changed
      ? {
          TenantId: tenantId,
          ProjectId: effectiveProjectId,
          CountryCode: countryFollowsProject ? null : countryCode,
        }
      : null

  return {
    tenantId,
    selectTenant,
    userTenants,
    projects,
    projectsLoading,
    effectiveProjectId,
    setProjectId,
    staleProject,
    effectiveCountryCode,
    setCountryCode,
    countryFollowsProject,
    values,
  }
}
